import * as TabsPrimitive from "@radix-ui/react-tabs";
import clsx from "clsx";
import { useCallback, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import { tabsListStyle, tabsTriggerStyle } from "./Tabs.css";
import { useTabsContext } from "./TabsContext";
import { TabsProvider } from "./TabsProvider";

export interface TabsPropsCustom extends TabsPrimitive.TabsProps {
  /**
   * If true, the entire tabs component is non-interactive.
   */
  disabled?: boolean;
}

export function Tabs({
  className,
  disabled,
  onValueChange,
  ...props
}: TabsPropsCustom) {
  const [value, setValue] = useState<string | undefined>(
    props.value || props.defaultValue
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      if (onValueChange) {
        onValueChange(newValue);
      }
    },
    [onValueChange]
  );

  return (
    <TabsProvider disabled={disabled} value={value}>
      <TabsPrimitive.Root
        onValueChange={handleValueChange}
        className={className}
        {...props}
      />
    </TabsProvider>
  );
}

interface TabsListProps extends TabsPrimitive.TabsListProps {
  /**
   * Shorthand: render each string as a trigger whose value and label are
   * identical. Omit to supply `<TabsTrigger>` children with distinct values.
   */
  triggers?: string[];
}

export function TabsList({
  className,
  triggers,
  children,
  ...props
}: TabsListProps) {
  return (
    <TabsPrimitive.List className={clsx(tabsListStyle, className)} {...props}>
      {triggers
        ? triggers.map((trigger, index) => (
            <Fragment key={index}>
              <TabsTrigger value={trigger}>{trigger}</TabsTrigger>
            </Fragment>
          ))
        : children}
    </TabsPrimitive.List>
  );
}

export function TabsTrigger({
  className,
  disabled,
  ...props
}: TabsPrimitive.TabsTriggerProps) {
  const { disabled: groupDisabled } = useTabsContext();

  return (
    <TabsPrimitive.Trigger
      className={clsx(tabsTriggerStyle, className)}
      disabled={disabled ?? groupDisabled}
      {...props}
    />
  );
}

export function TabsContent({
  className,
  ...props
}: TabsPrimitive.TabsContentProps) {
  return <TabsPrimitive.Content className={className} {...props} />;
}
